// Path: /var/www/speakiq/frontend/src/pages/SchoolHomepage.js
import MockTestsPublic from '../components/MockTestsPublic';
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981',
  warning: '#F59E0B', error: '#EF4444', grey: '#6B7280', border: '#E5E7EB',
};

const CEFR_COLORS = { A1:'#10B981', A2:'#34D399', B1:'#F59E0B', B2:'#F97316', C1:'#EF4444', C2:'#7C3AED' };
const DAYS_FULL = { mon:'Monday', tue:'Tuesday', wed:'Wednesday', thu:'Thursday', fri:'Friday', sat:'Saturday', sun:'Sunday' };
const DAY_ORDER = ['mon','tue','wed','thu','fri','sat','sun'];

function useApi(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!url) return;
    fetch(url).then(r => r.ok ? r.json() : Promise.reject()).then(setData).catch(() => setErr(true));
  }, [url]);
  return [data, err];
}

function Spinner() {
  return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh', flexDirection:'column', gap: 16 }}>
      <div style={{ width: 48, height: 48, border:`4px solid ${C.border}`, borderTop:`4px solid ${C.teal}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <p style={{ color: C.grey, fontSize: 14 }}>Loading…</p>
    </div>
  );
}

function NavBar({ profile, subdomain, tier }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [['Home','#hero'],['Courses','#courses'],['Tutors','#tutors'],['About','#about'],['Contact','#contact'],['Join as Tutor','#join-tutor']];
  const pc = profile?.primary_colour || C.teal;
  return (
    <nav style={{ position:'sticky', top: 0, zIndex: 100, background: C.navy, boxShadow:'0 2px 12px rgba(0,0,0,0.2)' }}>
      <style>{`
        @media (max-width: 768px) {
          .school-nav-links { display: none !important; }
          .school-nav-cta { display: none !important; }
          .school-nav-hamburger { display: flex !important; }
        }
      `}</style>
      <div style={{ maxWidth: 1200, margin:'0 auto', padding:'0 20px', display:'flex', alignItems:'center', height: 64, gap: 12 }}>
        {profile?.logo_url
          ? <img src={profile.logo_url} alt="logo" style={{ height: 38, borderRadius: 6, flexShrink:0 }} />
          : <div style={{ width: 38, height: 38, borderRadius: '50%', background: pc, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight: 700, fontSize: 17, flexShrink: 0 }}>
              {(profile?.name || 'S').charAt(0)}
            </div>
        }
        <span style={{ color:'#fff', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>{profile?.name || 'English School'}</span>

        {/* Desktop links */}
        <div className="school-nav-links" style={{ marginLeft:'auto', display:'flex', gap: 22, alignItems:'center' }}>
          {links.map(([label, href]) => (
            <a key={label} href={href} style={{ color:'rgba(255,255,255,0.75)', textDecoration:'none', fontSize: 14, transition:'color 0.15s' }}
              onMouseEnter={e => e.target.style.color='#fff'} onMouseLeave={e => e.target.style.color='rgba(255,255,255,0.75)'}>
              {label}
            </a>
          ))}
        </div>
        <div className="school-nav-cta" style={{ display:'flex', gap: 10, marginLeft: 20 }}>
          <a href={`/register?school=${subdomain}`} style={{ background:'#10B981', color:'#fff', textDecoration:'none', fontSize: 13, padding:'7px 14px', borderRadius: 6, fontWeight: 600 }}>🎓 Free Assessment</a>
          <a href="/login" style={{ color:'rgba(255,255,255,0.75)', textDecoration:'none', fontSize: 13, padding:'7px 12px', border:`1px solid rgba(255,255,255,0.2)`, borderRadius: 6 }}>Login</a>
          <a href={`/register?school=${subdomain}`} style={{ background: pc, color:'#fff', textDecoration:'none', fontSize: 13, padding:'7px 14px', borderRadius: 6, fontWeight: 600 }}>Register →</a>
        </div>

        {/* Mobile hamburger */}
        <button className="school-nav-hamburger" onClick={() => setMenuOpen(o => !o)}
          style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#fff', padding: 6, display:'none', flexDirection:'column', gap: 5 }}>
          {menuOpen
            ? <span style={{ fontSize: 22, lineHeight:1 }}>✕</span>
            : <>{[0,1,2].map(i => <span key={i} style={{ display:'block', width:22, height:2, background:'#fff', borderRadius:2 }} />)}</>}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {menuOpen && (
        <div style={{ background: C.navy, borderTop:'1px solid rgba(255,255,255,0.1)', padding:'8px 0' }}>
          {links.map(([label, href]) => (
            <a key={label} href={href} onClick={() => setMenuOpen(false)}
              style={{ display:'block', color:'rgba(255,255,255,0.85)', textDecoration:'none', fontSize:14, fontWeight:500, padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
              {label}
            </a>
          ))}
          <div style={{ display:'flex', gap:10, padding:'12px 20px' }}>
            <a href="/login" style={{ flex:1, textAlign:'center', color:'rgba(255,255,255,0.8)', textDecoration:'none', fontSize:14, padding:'9px 0', border:'1px solid rgba(255,255,255,0.2)', borderRadius:6 }}>Login</a>
            <a href={`/register?school=${subdomain}`} style={{ flex:1, textAlign:'center', background: pc, color:'#fff', textDecoration:'none', fontSize:14, padding:'9px 0', borderRadius:6, fontWeight:600 }}>Register →</a>
          </div>
        </div>
      )}
    </nav>
  );
}

function HeroSection({ profile, stats, subdomain }) {
  const { t } = useTranslation();
  const pc = profile?.primary_colour || C.teal;
  const bg = profile?.hero_image_url
    ? `linear-gradient(rgba(13,27,42,0.65),rgba(13,27,42,0.65)), url(${profile.hero_image_url}) center/cover`
    : `linear-gradient(135deg, ${C.navy} 0%, ${pc} 100%)`;
  return (
    <section id="hero" style={{ background: bg, padding:'clamp(48px, 10vw, 100px) 20px clamp(40px, 8vw, 80px)', textAlign:'center' }}>
      <div style={{ maxWidth: 800, margin:'0 auto' }}>
        <h1 style={{ color:'#fff', fontSize: 'clamp(32px,5vw,58px)', fontWeight: 800, margin:'0 0 16px', lineHeight: 1.15 }}>
          {profile?.name || 'Your English Learning Centre'}
        </h1>
        <p style={{ color:'rgba(255,255,255,0.88)', fontSize: 'clamp(16px,2vw,20px)', margin:'0 0 36px', maxWidth: 600, marginLeft:'auto', marginRight:'auto' }}>
          {profile?.tagline || 'Learn English from A1 to C2 with expert tutors and AI-powered practice'}
        </p>
        <div style={{ display:'flex', gap: 14, justifyContent:'center', flexWrap:'wrap', marginBottom: 48 }}>
          {profile?.registration_open !== false
            ? <a href={`/register?school=${subdomain}`} style={{ background:'#fff', color: pc, padding:'14px 32px', borderRadius: 8, fontWeight: 700, fontSize: 16, textDecoration:'none' }}>{t('school_enroll_now')}</a>
            : <div style={{ background:'rgba(255,255,255,0.15)', color:'rgba(255,255,255,0.8)', padding:'14px 32px', borderRadius: 8, fontWeight: 600, fontSize: 15, border:'2px solid rgba(255,255,255,0.3)' }}>
                {profile?.registration_message || 'Registration currently closed'}
              </div>
          }
          <a href="#courses" style={{ border:'2px solid rgba(255,255,255,0.7)', color:'#fff', padding:'14px 32px', borderRadius: 8, fontWeight: 600, fontSize: 16, textDecoration:'none' }}>{t('school_learn_more')}</a>
          <a href={`/register?school=${subdomain}`} style={{ background:'#10B981', color:'#fff', padding:'14px 32px', borderRadius: 8, fontWeight: 700, fontSize: 16, textDecoration:'none' }}>🎓 Free Assessment — Sign Up</a>
        </div>
        <div style={{ display:'flex', gap: 32, justifyContent:'center', flexWrap:'wrap' }}>
          {[
            { val: stats?.total_students ?? '500', label:'Students Enrolled' },
            { val: stats?.total_tutors ?? '10', label:'Expert Tutors' },
            { val: stats?.total_certificates ?? '200', label:'Certificates Issued' },
          ].map(({ val, label }) => (
            <div key={label} style={{ textAlign:'center' }}>
              <div style={{ color:'#fff', fontSize: 32, fontWeight: 800 }}>{val}+</div>
              <div style={{ color:'rgba(255,255,255,0.7)', fontSize: 13 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 40, animation:'bounce 2s infinite' }}>
        <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(8px)}}`}</style>
        <span style={{ color:'rgba(255,255,255,0.5)', fontSize: 24 }}>↓</span>
      </div>
    </section>
  );
}

function WhyUs({ pc }) {
  const { t } = useTranslation();
  const cards = [
    { icon:'🤖', title:'AI-Powered Practice', text:'Practice English anytime with our AI system, tailored to your level' },
    { icon:'👩‍🏫', title:'Expert Human Tutors', text:'Qualified tutors for group classes, IELTS and Business English — up to 12 students per group' },
    { icon:'🏆', title:'CEFR Certification', text:'Internationally recognised level certificates from A1 to C2' },
  ];
  return (
    <section style={{ background:'#f9fafb', padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)' }}>
      <div style={{ maxWidth: 1100, margin:'0 auto' }}>
        <h2 style={{ textAlign:'center', color: C.navy, fontSize: 32, fontWeight: 700, marginBottom: 48 }}>{t('school_why_us')}</h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap: 24 }}>
          {cards.map(c => (
            <div key={c.title} style={{ background:'#fff', borderRadius: 12, padding: 32, textAlign:'center', border:`1px solid ${C.border}`, boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>{c.icon}</div>
              <h3 style={{ color: C.navy, fontSize: 20, fontWeight: 700, marginBottom: 10 }}>{c.title}</h3>
              <p style={{ color: C.grey, fontSize: 15, lineHeight: 1.6, margin: 0 }}>{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const COURSE_TIERS = [
  {
    key: 'ai',
    icon: '🤖',
    label: 'AI Practice Only',
    sublabel: 'Self-paced · No human tutor · Supervised at your centre',
    desc: 'Structured AI practice sessions at the learning centre. No scheduled tutor classes — students work through the platform at their own pace. Sold as a fixed access period, not monthly.',
    color: '#00B4D8',
    courses: [
      { icon:'🤖', title:'AI Practice', price:'$39/mo', upfront:'$398 for 12 months', desc:'Up to 1.5 hours of supervised AI practice per day. All 7 skills: Grammar, Vocabulary, Reading, Writing, Conversation, IELTS and Business. CEFR level assessment and progress tracking included.', tag:null },
      { icon:'🎯', title:'AI IELTS Prep', price:'$69/mo', upfront:'$352 for 6 months', desc:'Up to 1.5 hours of IELTS-focused AI practice per day. Writing tasks graded on official IELTS band descriptors. All 4 IELTS skills covered daily. Full mock tests with band score estimates.', tag:'IELTS' },
      { icon:'💼', title:'AI Business English', price:'$69/mo', upfront:'$352 for 6 months', desc:'Up to 1.5 hours of Business English AI practice per day. Professional email writing, meeting language, presentations and negotiations. Business vocabulary by industry sector.', tag:'Business' },
    ],
  },
  {
    key: 'online',
    icon: '🖥️',
    label: 'Online Tutor Classes',
    sublabel: 'Live video · Qualified tutors · For students abroad or remote',
    desc: 'Live video sessions with a qualified tutor. Same CEFR roadmap and AI practice platform as physical attendance. Perfect for students who cannot come in person.',
    color: '#06B6D4',
    courses: [
      { icon:'🖥️', title:'Online Group Classes', price:'$59/mo', upfront:null, desc:'12 live group video sessions per month (90 min, 3x/week). Up to 12 students per group, CEFR level matched. AI practice platform included. Pay per level.', tag:null },
      { icon:'⭐', title:'Online Standard', price:'$85/mo', upfront:null, desc:'12 live group video sessions per month plus daily AI practice. Up to 12 students per group. Tutor reviews AI progress before every class. CEFR assessments and certificates included.', tag:'Most Popular Online' },
      { icon:'💎', title:'Online Premium Group', price:'$129/mo', upfront:null, desc:'12 premium group video sessions per month (90 min, 3x/week). Up to 12 students per group. Tutor tailors every session to the group\'s pace. Daily AI practice included.', tag:null },
      { icon:'🏆', title:'Online IELTS Group', price:'$199/mo', upfront:null, desc:'20 live IELTS group sessions per month (90 min, 5x/week). Up to 12 students, IELTS band matched. Writing and speaking reviewed every class. Full mock test every 2 weeks.', tag:'Best for IELTS' },
    ],
  },
  {
    key: 'physical',
    icon: '🏫',
    label: 'Physical Centre',
    sublabel: 'In-person · At your learning centre',
    desc: 'Attend the learning centre in person. CEFR-structured classes with a qualified tutor, plus supervised AI practice sessions every day. Pay per level — monthly or upfront.',
    color: '#10B981',
    courses: [
      { icon:'👥', title:'Group Classes', price:'$59/mo', upfront:null, desc:'12 group classes per month at the centre (90 min, 3x/week). Up to 12 students per group at the same CEFR level. AI practice platform included between classes. Pay per level.', tag:null },
      { icon:'⭐', title:'Standard', price:'$99/mo', upfront:null, desc:'12 group classes per month plus up to 2 hours supervised AI practice daily. Up to 12 students per group. Tutor sees your full AI history — every class is personalised. CEFR certificates included.', tag:'Most Popular' },
      { icon:'💎', title:'Premium Group', price:'$159/mo', upfront:null, desc:'12 premium group sessions per month (90 min, 3x/week). Up to 12 students per group. Tutor tailors every class to the group\'s pace. Up to 2 hours supervised AI practice per day.', tag:null },
      { icon:'🏆', title:'IELTS Group Intensive', price:'$249/mo', upfront:null, desc:'20 IELTS group sessions per month (90 min, 5x/week). Up to 12 students, band matched. Writing and speaking reviewed every class. Full mock test every 2 weeks. IELTS AI practice daily.', tag:'Best for IELTS' },
      { icon:'🏢', title:'Corporate Group', price:'$299/mo', upfront:null, desc:'12 Business English group sessions per month. Up to 12 students, industry matched. Presentation coaching, business writing, negotiation and meetings English. Corporate certificate included.', tag:'Enterprise' },
    ],
  },
];

function PublicConverter({ pc }) {
  const color = pc || C.teal;
  const META = { UZS:{flag:'🇺🇿',dp:0}, USD:{flag:'🇺🇸',dp:2}, EUR:{flag:'🇪🇺',dp:2}, GBP:{flag:'🇬🇧',dp:2}, RUB:{flag:'🇷🇺',dp:2}, KZT:{flag:'🇰🇿',dp:2} };
  const [rates, setRates] = useState(null);
  const [amount, setAmount] = useState('59');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('UZS');
  useEffect(() => { fetch('/public/rates').then(r => r.ok ? r.json() : null).then(setRates).catch(() => {}); }, []);
  if (!rates || !rates.rates) return null;
  const list = rates.currencies || Object.keys(META);
  const rt = rates.rates;
  const amt = parseFloat(amount) || 0;
  const result = (rt[from] && rt[to]) ? amt * (rt[to] / rt[from]) : null;
  const fmt = (v, c) => v == null ? '—' : new Intl.NumberFormat('en-US', { minimumFractionDigits: META[c]?.dp ?? 2, maximumFractionDigits: META[c]?.dp ?? 2 }).format(v);
  const sel = { padding:'11px 12px', borderRadius:10, border:`1px solid ${C.border}`, fontSize:15, fontWeight:600, color:C.navy, background:'#fff', cursor:'pointer', width:'100%' };
  const Opt = ({ c }) => <option value={c}>{META[c]?.flag} {c}</option>;
  return (
    <section style={{ padding:'clamp(32px,6vw,56px) clamp(16px,4vw,24px)', background:'#F8FAFC' }}>
      <div style={{ maxWidth: 720, margin:'0 auto', textAlign:'center' }}>
        <h2 style={{ fontSize:'clamp(22px,4vw,30px)', fontWeight:800, color:C.navy, margin:'0 0 6px' }}>💱 See fees in your currency</h2>
        <p style={{ color:C.grey, margin:'0 0 22px', fontSize:15 }}>Live exchange rate — check any course price in the currency you know.</p>
        <div style={{ background:'#fff', borderRadius:16, padding:'clamp(18px,4vw,28px)', border:`1px solid ${C.border}`, boxShadow:'0 4px 20px rgba(0,0,0,0.05)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr auto 1fr', gap:10, alignItems:'end' }}>
            <div style={{ textAlign:'left' }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.grey, marginBottom:5 }}>AMOUNT</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} style={{ ...sel, cursor:'text' }} />
            </div>
            <div style={{ textAlign:'left' }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.grey, marginBottom:5 }}>FROM</label>
              <select value={from} onChange={e => setFrom(e.target.value)} style={sel}>{list.map(c => <Opt key={c} c={c} />)}</select>
            </div>
            <button onClick={() => { setFrom(to); setTo(from); }} title="Swap" style={{ padding:'11px 13px', borderRadius:10, border:`1px solid ${C.border}`, background:'#F8FAFC', cursor:'pointer', fontSize:17 }}>⇄</button>
            <div style={{ textAlign:'left' }}>
              <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.grey, marginBottom:5 }}>TO</label>
              <select value={to} onChange={e => setTo(e.target.value)} style={sel}>{list.map(c => <Opt key={c} c={c} />)}</select>
            </div>
          </div>
          <div style={{ marginTop:18, padding:18, borderRadius:12, background:`${color}10`, border:`1px solid ${color}30` }}>
            <div style={{ fontSize:14, color:C.grey }}>{fmt(amt, from)} {from} =</div>
            <div style={{ fontSize:'clamp(24px,5vw,32px)', fontWeight:800, color:C.navy, lineHeight:1.2 }}>{fmt(result, to)} <span style={{ fontSize:17, color:C.grey, fontWeight:700 }}>{to}</span></div>
          </div>
          <div style={{ fontSize:11.5, color:'#94A3B8', marginTop:12, lineHeight:1.5 }}>
            Approximate — course fees are charged in USD. Official rate{rates.cbu_date ? ` (${rates.cbu_date})` : ''} from the Central Bank of Uzbekistan.
          </div>
        </div>
      </div>
    </section>
  );
}

function CoursesSection({ packages, showPrices, subdomain, pc }) {
  const { t } = useTranslation();
  const [activeTier, setActiveTier] = useState('ai');
  const hasPricePackages = showPrices && packages && packages.length > 0;
  const color = pc || C.teal;
  const tier = COURSE_TIERS.find(t => t.key === activeTier);

  return (
    <section id="courses" style={{ padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)', background:'#fff' }}>
      <div style={{ maxWidth: 1100, margin:'0 auto' }}>
        <h2 style={{ textAlign:'center', color: C.navy, fontSize: 32, fontWeight: 700, marginBottom: 8 }}>{t('school_our_courses')}</h2>
        <p style={{ textAlign:'center', color: C.grey, marginBottom: 40 }}>Choose the programme that fits your goals</p>

        {hasPricePackages ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap: 24 }}>
            {packages.map((p, i) => (
              <div key={p.id || i} style={{ border:`2px solid ${i === 1 ? color : C.border}`, borderRadius: 12, padding: 28, textAlign:'center', position:'relative', background: i === 1 ? `${color}08` : '#fff' }}>
                {i === 1 && <div style={{ position:'absolute', top:-12, left:'50%', transform:'translateX(-50%)', background: color, color:'#fff', fontSize: 11, padding:'3px 12px', borderRadius: 20, fontWeight: 600 }}>POPULAR</div>}
                <h3 style={{ color: C.navy, fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{p.name}</h3>
                <div style={{ color: color, fontSize: 32, fontWeight: 800, marginBottom: 16 }}>${p.price}<span style={{ fontSize: 14, fontWeight: 400, color: C.grey }}>/mo</span></div>
                <a href={`/register?school=${subdomain}&package=${p.id}`} style={{ display:'block', marginTop: 20, background: i === 1 ? color : 'transparent', color: i === 1 ? '#fff' : color, border:`2px solid ${color}`, borderRadius: 8, padding:'10px 0', fontWeight: 600, fontSize: 14, textDecoration:'none' }}>Enrol Now</a>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Tier tab buttons */}
            <div style={{ display:'flex', gap: 8, justifyContent:'center', marginBottom: 24, flexWrap:'wrap' }}>
              {COURSE_TIERS.map(tr => (
                <button key={tr.key} onClick={() => setActiveTier(tr.key)}
                  style={{
                    padding:'11px 22px', border:'none', borderRadius: 10, cursor:'pointer',
                    fontWeight: 700, fontSize: 14,
                    background: activeTier === tr.key ? (color) : '#F1F5F9',
                    color: activeTier === tr.key ? '#fff' : C.grey,
                    transition:'all 0.15s',
                  }}>
                  {tr.icon} {tr.label}
                </button>
              ))}
            </div>

            {/* Tier description banner */}
            {tier && (
              <div style={{ background:'#F8FAFC', border:`1px solid ${C.border}`, borderRadius: 12, padding:'14px 20px', marginBottom: 32, textAlign:'center' }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: C.navy, marginBottom: 4 }}>
                  {tier.icon} {tier.label} <span style={{ fontWeight: 400, color: C.grey, fontSize: 13 }}>— {tier.sublabel}</span>
                </div>
                <div style={{ fontSize: 13, color: C.grey }}>{tier.desc}</div>
              </div>
            )}

            {/* Course cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap: 24 }}>
              {(tier?.courses || []).map((c, i) => (
                <div key={i} style={{ border:`1.5px solid ${c.tag ? color : C.border}`, borderRadius: 12, padding: 28, background:'#fff', position:'relative', transition:'box-shadow 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.10)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
                  {c.tag && (
                    <div style={{ position:'absolute', top:-12, left:20, background: color, color:'#fff', fontSize: 11, padding:'3px 12px', borderRadius: 20, fontWeight: 600 }}>
                      {c.tag}
                    </div>
                  )}
                  <div style={{ fontSize: 36, marginBottom: 10 }}>{c.icon}</div>
                  <h3 style={{ color: C.navy, fontSize: 16, fontWeight: 800, marginBottom: 4 }}>{c.title}</h3>
                  <div style={{ fontSize: 24, fontWeight: 800, color: color, marginBottom: 2, lineHeight: 1 }}>{c.price}</div>
                  {c.upfront
                    ? <div style={{ fontSize: 12, color: color, fontWeight: 600, marginBottom: 10 }}>{c.upfront}</div>
                    : <div style={{ marginBottom: 10 }} />}
                  <p style={{ color: C.grey, fontSize: 13, lineHeight: 1.6, marginBottom: 20 }}>{c.desc}</p>
                  <a href={`/register?school=${subdomain}`} style={{ display:'inline-block', background: color, color:'#fff', padding:'10px 22px', borderRadius: 8, fontWeight: 600, fontSize: 13, textDecoration:'none' }}>Enquire Now →</a>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n:1, title:'Register Online', text:'Create your account and choose your programme' },
    { n:2, title:'Visit the Centre', text:'Meet your tutor and take a free level assessment' },
    { n:3, title:'Start Learning', text:'Join classes, use AI practice and track your progress' },
  ];
  return (
    <section style={{ background:'#f0f9ff', padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)' }}>
      <div style={{ maxWidth: 900, margin:'0 auto' }}>
        <h2 style={{ textAlign:'center', color: C.navy, fontSize: 32, fontWeight: 700, marginBottom: 48 }}>How It Works</h2>
        <div style={{ display:'flex', alignItems:'flex-start', gap: 0, justifyContent:'center', flexWrap:'wrap' }}>
          {steps.map((s, i) => (
            <React.Fragment key={s.n}>
              <div style={{ textAlign:'center', flex:'0 0 220px', padding:'0 16px' }}>
                <div style={{ width: 60, height: 60, borderRadius:'50%', background: C.teal, color:'#fff', fontSize: 24, fontWeight: 800, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>{s.n}</div>
                <h3 style={{ color: C.navy, fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{s.title}</h3>
                <p style={{ color: C.grey, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{s.text}</p>
              </div>
              {i < 2 && <div style={{ fontSize: 32, color: C.teal, alignSelf:'flex-start', marginTop: 14, flexShrink: 0 }}>→</div>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function TutorsSection({ tutors, subdomain, pc }) {
  const { t } = useTranslation();
  const shown = (tutors || []).slice(0, 6);
  return (
    <section id="tutors" style={{ padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)', background:'#fff' }}>
      <div style={{ maxWidth: 1100, margin:'0 auto' }}>
        <h2 style={{ textAlign:'center', color: C.navy, fontSize: 32, fontWeight: 700, marginBottom: 8 }}>{t('school_our_tutors')}</h2>
        <p style={{ textAlign:'center', color: C.grey, marginBottom: 48 }}>Meet the experts who will guide your journey</p>
        {shown.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>👩‍🏫</div>
            <h3 style={{ color: C.navy, fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Our tutors are being recruited</h3>
            <p style={{ color: C.grey, fontSize: 15, marginBottom: 24 }}>We're building an exceptional team of qualified tutors. Check back soon!</p>
            <a href="/tutor-signup" style={{ background: pc || C.teal, color:'#fff', padding:'12px 28px', borderRadius: 8, textDecoration:'none', fontWeight: 600 }}>Apply to Teach Here →</a>
          </div>
        ) : (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap: 20 }}>
              {shown.map((tutor, i) => (
                <div key={tutor.id || i} style={{ textAlign:'center', padding: 20, border:`1px solid ${C.border}`, borderRadius: 12, background:'#fafafa' }}>
                  {tutor.photo_url
                    ? <img src={tutor.photo_url} alt={tutor.name} style={{ width: 72, height: 72, borderRadius:'50%', objectFit:'cover', marginBottom: 12 }} />
                    : <div style={{ width: 72, height: 72, borderRadius:'50%', background: pc || C.teal, color:'#fff', fontSize: 24, fontWeight: 700, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>{(tutor.name||'T').charAt(0)}</div>
                  }
                  <div style={{ fontWeight: 700, color: C.navy, fontSize: 15, marginBottom: 6 }}>{tutor.name}</div>
                  {tutor.rating > 0 && <div style={{ color: C.warning, fontSize: 13 }}>{'\u2605'.repeat(Math.round(tutor.rating))} {tutor.rating}</div>}
                  {tutor.specializations?.slice(0,2).map(sp => (
                    <span key={sp} style={{ display:'inline-block', background:`${pc}18`, color: pc || C.teal, fontSize: 11, padding:'2px 8px', borderRadius: 12, margin:'2px', fontWeight: 600 }}>
                      {sp.replace(/[_]/g,' ').replace(/\b\w/g, l => l.toUpperCase()).replace(/\bIelts\b/g,'IELTS').replace(/\bAi\b/g,'AI')}
                    </span>
                  ))}
                </div>
              ))}
            </div>
            {(tutors || []).length > 6 && (
              <div style={{ textAlign:'center', marginTop: 32 }}>
                <a href={`/school/${subdomain}/tutors`} style={{ color: pc || C.teal, fontWeight: 600, textDecoration:'none', fontSize: 15 }}>Meet All Our Tutors →</a>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function TestimonialsSection({ testimonials }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => {
    if (!testimonials?.length) return;
    timerRef.current = setInterval(() => setIdx(i => (i + 1) % testimonials.length), 5000);
    return () => clearInterval(timerRef.current);
  }, [testimonials?.length]);
  if (!testimonials?.length) return null;
  const t = testimonials[idx];
  return (
    <section style={{ background: C.navy, padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)', textAlign:'center' }}>
      <div style={{ maxWidth: 700, margin:'0 auto' }}>
        <h2 style={{ color:'#fff', fontSize: 32, fontWeight: 700, marginBottom: 48 }}>What Our Students Say</h2>
        <div style={{ position:'relative', minHeight: 180 }}>
          <div style={{ fontSize: 72, color: C.teal, lineHeight: 1, marginBottom:'-20px', opacity: 0.5 }}>"</div>
          <p style={{ color:'rgba(255,255,255,0.9)', fontSize: 18, lineHeight: 1.7, fontStyle:'italic', margin:'0 0 24px' }}>{t.text}</p>
          <div style={{ color:'#fff', fontWeight: 700, fontSize: 15 }}>{t.name}</div>
          <span style={{ background: CEFR_COLORS[t.cefr] || C.teal, color:'#fff', fontSize: 12, padding:'2px 10px', borderRadius: 12, fontWeight: 600, marginTop: 6, display:'inline-block' }}>{t.cefr} Achieved</span>
          <div style={{ color: C.warning, fontSize: 18, marginTop: 8 }}>{'★'.repeat(t.rating || 5)}</div>
        </div>
        <div style={{ display:'flex', gap: 8, justifyContent:'center', marginTop: 24 }}>
          {testimonials.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} style={{ width: 10, height: 10, borderRadius:'50%', background: i === idx ? C.teal : 'rgba(255,255,255,0.3)', border:'none', cursor:'pointer', padding: 0 }} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CEFRPath() {
  const levels = ['A1','A2','B1','B2','C1','C2'];
  return (
    <section style={{ background:'#f9fafb', padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)' }}>
      <div style={{ maxWidth: 1000, margin:'0 auto', textAlign:'center' }}>
        <h2 style={{ color: C.navy, fontSize: 32, fontWeight: 700, marginBottom: 8 }}>Your Learning Journey</h2>
        <p style={{ color: C.grey, marginBottom: 48 }}>Earn a recognised certificate at every level.</p>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', flexWrap:'wrap', gap: 0 }}>
          {levels.map((l, i) => (
            <React.Fragment key={l}>
              <div style={{ textAlign:'center' }}>
                <div style={{ width: 64, height: 64, borderRadius:'50%', background: CEFR_COLORS[l], color:'#fff', fontSize: 18, fontWeight: 800, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px ${CEFR_COLORS[l]}60`, margin:'0 auto 8px' }}>{l}</div>
                <div style={{ fontSize: 11, color: C.grey }}>{['Beginner','Elementary','Intermediate','Upper-Int.','Advanced','Proficient'][i]}</div>
              </div>
              {i < 5 && <div style={{ width: 40, height: 3, background: `linear-gradient(90deg, ${CEFR_COLORS[levels[i]]}, ${CEFR_COLORS[levels[i+1]]})`, marginBottom: 20, flexShrink: 0 }} />}
            </React.Fragment>
          ))}
        </div>
        <div style={{ marginTop: 48, background:'#fff', borderRadius: 12, padding: 24, maxWidth: 340, margin:'40px auto 0', border:`2px dashed ${C.border}`, textAlign:'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🏆</div>
          <div style={{ fontWeight: 700, color: C.navy, fontSize: 16, marginBottom: 4 }}>Certificate of Achievement</div>
          <div style={{ color: C.grey, fontSize: 13 }}>B2 — Upper Intermediate</div>
          <div style={{ color: C.grey, fontSize: 12, marginTop: 4 }}>Issued by Your School · 2026</div>
        </div>
      </div>
    </section>
  );
}

function AboutSection({ profile }) {
  if (!profile?.description && !profile?.founded_year && !profile?.gallery_images?.length) return null;
  return (
    <section id="about" style={{ padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)', background:'#fff' }}>
      <div style={{ maxWidth: 1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: 48 }}>
        <div>
          <h2 style={{ color: C.navy, fontSize: 32, fontWeight: 700, marginBottom: 20 }}>About Us</h2>
          <p style={{ color: C.grey, fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{profile.description || 'We are dedicated to helping students achieve their English language goals through expert instruction and innovative technology.'}</p>
          {profile.founded_year && (
            <div style={{ display:'flex', gap: 16, alignItems:'center', background:'#f0f9ff', padding: 20, borderRadius: 10, borderLeft:`4px solid ${C.teal}` }}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize: 40, fontWeight: 800, color: C.teal }}>{new Date().getFullYear() - parseInt(profile.founded_year)}</div>
                <div style={{ color: C.grey, fontSize: 12 }}>Years of Excellence</div>
              </div>
              <div style={{ color: C.grey, fontSize: 14 }}>Founded in {profile.founded_year}, we have been transforming English education.</div>
            </div>
          )}
        </div>
        {profile.gallery_images?.length > 0 && (
          <div style={{ columns: 2, columnGap: 12 }}>
            {profile.gallery_images.slice(0,6).map((url, i) => (
              <img key={i} src={url} alt="" style={{ width:'100%', borderRadius: 8, marginBottom: 12, objectFit:'cover', display:'block', breakInside:'avoid' }}
                onError={e => e.target.style.display='none'} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function IELTSSection({ pc }) {
  const features = ['Targeted Band 6.0–7.5 preparation', 'Full mock tests with detailed feedback', 'Listening, Reading, Writing & Speaking modules', 'Small group sessions up to 12 students per group', 'Proven track record of score improvements'];
  return (
    <section style={{ background:`linear-gradient(135deg, ${C.navy}, #1a3a5c)`, padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)' }}>
      <div style={{ maxWidth: 800, margin:'0 auto', textAlign:'center' }}>
        <div style={{ display:'inline-block', background: C.warning, color:'#fff', padding:'4px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, marginBottom: 16 }}>IELTS PREPARATION</div>
        <h2 style={{ color:'#fff', fontSize: 32, fontWeight: 700, marginBottom: 16 }}>Preparing for IELTS?</h2>
        <p style={{ color:'rgba(255,255,255,0.8)', fontSize: 18, marginBottom: 40 }}>Our intensive programme targets Band 6.0–7.5 with structured modules and expert coaching.</p>
        <ul style={{ listStyle:'none', padding: 0, margin:'0 0 36px', textAlign:'left', maxWidth: 400, marginLeft:'auto', marginRight:'auto' }}>
          {features.map(f => (
            <li key={f} style={{ color:'rgba(255,255,255,0.85)', fontSize: 15, padding:'8px 0', borderBottom:`1px solid rgba(255,255,255,0.1)`, paddingLeft: 24, position:'relative' }}>
              <span style={{ position:'absolute', left: 0, color: C.success }}>✓</span> {f}
            </li>
          ))}
        </ul>
        <a href="#contact" style={{ background: pc || C.teal, color:'#fff', padding:'14px 32px', borderRadius: 8, textDecoration:'none', fontWeight: 700, fontSize: 16 }}>Enquire About IELTS →</a>
      </div>
    </section>
  );
}

function FindUs({ profile }) {
  const DAY_ORDER_LOCAL = ['mon','tue','wed','thu','fri','sat','sun'];
  const rawDays = profile?.working_hours || {};
  const days = DAY_ORDER_LOCAL.filter(k => k in rawDays).map(k => [k, rawDays[k]]);
  return (
    <section id="contact" style={{ padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)', background:'#f9fafb' }}>
      <div style={{ maxWidth: 1100, margin:'0 auto' }}>
        <h2 style={{ textAlign:'center', color: C.navy, fontSize: 32, fontWeight: 700, marginBottom: 48 }}>Find Us</h2>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 40 }}>
          <div>
            {(profile?.address?.line1 || profile?.city) && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, color: C.navy, marginBottom: 6 }}>📍 Address</div>
                <div style={{ color: C.grey, fontSize: 15, lineHeight: 1.7 }}>
                  {profile.address?.line1}{profile.address?.line2 && <><br />{profile.address?.line2}</>}
                  {(profile.city || profile.region) && <><br />{[profile.city, profile.region].filter(Boolean).join(', ')}</>}
                </div>
              </div>
            )}
            {profile?.phone && <div style={{ marginBottom: 12 }}><span style={{ fontWeight: 700, color: C.navy }}>📞 Phone: </span><a href={`tel:${profile.phone}`} style={{ color: C.teal, textDecoration:'none' }}>{profile.phone}</a></div>}
            {profile?.whatsapp && <div style={{ marginBottom: 12 }}><span style={{ fontWeight: 700, color: C.navy }}>💬 WhatsApp: </span><a href={`https://wa.me/${profile.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={{ color: C.success, textDecoration:'none' }}>{profile.whatsapp}</a></div>}
            {profile?.public_email && <div style={{ marginBottom: 24 }}><span style={{ fontWeight: 700, color: C.navy }}>✉️ Email: </span><a href={`mailto:${profile.public_email}`} style={{ color: C.teal, textDecoration:'none' }}>{profile.public_email}</a></div>}
            {days.length > 0 && (
              <div>
                <div style={{ fontWeight: 700, color: C.navy, marginBottom: 10 }}>🕐 Working Hours</div>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize: 14 }}>
                  <tbody>
                    {days.map(([d, h]) => (
                      <tr key={d} style={{ borderBottom:`1px solid ${C.border}` }}>
                        <td style={{ padding:'6px 0', color: C.navy, fontWeight: 500 }}>{DAYS_FULL[d] || d}</td>
                        <td style={{ padding:'6px 0', color: h.open ? C.grey : C.error, textAlign:'right' }}>
                          {h.open ? `${h.from} – ${h.to}` : 'Closed'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div>
            {(() => {
              const embed = profile?.google_maps_embed || '';
              const isValid = embed.startsWith('https://www.google.com/maps/embed') || embed.startsWith('https://maps.google.com/maps?');
              if (isValid) {
                return <iframe src={embed} width="100%" height="360" style={{ border: 0, borderRadius: 12 }} allowFullScreen loading="lazy" title="Map" />;
              }
              // Fallback: show address card
              return (
                <div style={{ background: '#F9FAFB', borderRadius: 12, border: '1px solid ' + C.border, padding: '32px 24px', minHeight: 200 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📍</div>
                  <div style={{ fontWeight: 700, fontSize: 17, color: C.navy, marginBottom: 8 }}>{profile?.name || 'Our School'}</div>
                  {profile?.address?.line1 && <div style={{ color: C.grey, fontSize: 14, marginBottom: 2 }}>{profile.address.line1}</div>}
                  {profile?.address?.line2 && <div style={{ color: C.grey, fontSize: 14, marginBottom: 2 }}>{profile.address.line2}</div>}
                  {profile?.city && <div style={{ color: C.grey, fontSize: 14, marginBottom: 8 }}>{profile.city}{profile.region ? ', ' + profile.region : ''}</div>}
                  {profile?.phone && <div style={{ color: C.navy, fontSize: 14, fontWeight: 600 }}>📞 {profile.phone}</div>}
                  {profile?.whatsapp && <div style={{ color: '#25D366', fontSize: 14, marginTop: 4 }}>💬 WhatsApp: {profile.whatsapp}</div>}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}

function EnquiryForm({ subdomain, packages }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name:'', email:'', phone:'', programme:'', message:'' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const genericPrograms = ['General English', 'IELTS Preparation', 'Business English', 'Group Classes', 'Kids English'];
  const programs = packages?.length ? packages.map(p => p.name) : genericPrograms;
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await fetch(`/public/${subdomain}/enquiry`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(form) });
      setSent(true);
    } catch { setSent(true); }
    finally { setSending(false); }
  };
  const inp2 = { width:'100%', padding:'10px 14px', border:`1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing:'border-box', outline:'none', fontFamily:'inherit' };
  return (
    <section style={{ padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)', background:'#fff' }}>
      <div style={{ maxWidth: 600, margin:'0 auto' }}>
        <h2 style={{ textAlign:'center', color: C.navy, fontSize: 32, fontWeight: 700, marginBottom: 8 }}>Send Us a Message</h2>
        <p style={{ textAlign:'center', color: C.grey, marginBottom: 36 }}>We'll get back to you within 24 hours</p>
        {sent ? (
          <div style={{ textAlign:'center', padding: 40, background:'#f0fdf4', borderRadius: 12, border:`1px solid ${C.success}` }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h3 style={{ color: C.success, fontSize: 22, marginBottom: 8 }}>Thank you!</h3>
            <p style={{ color: C.grey }}>We'll be in touch within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap: 16 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 16 }}>
              <input style={inp2} required placeholder="Your Name" value={form.name} onChange={e => set('name', e.target.value)} />
              <input style={inp2} type="email" required placeholder="Email Address" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <input style={inp2} placeholder="Phone Number" value={form.phone} onChange={e => set('phone', e.target.value)} />
            <select style={inp2} value={form.programme} onChange={e => set('programme', e.target.value)}>
              <option value="">Which programme interests you?</option>
              {programs.map(p => <option key={p}>{p}</option>)}
            </select>
            <textarea style={{ ...inp2, height: 120, resize:'vertical' }} placeholder="Your message…" value={form.message} onChange={e => set('message', e.target.value)} />
            <button type="submit" disabled={sending} style={{ background: C.teal, color:'#fff', border:'none', borderRadius: 8, padding:'14px', fontSize: 15, fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer' }}>
              {sending ? t('status_loading') : t('school_send_message') + ' →'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

function Footer({ profile, tier }) {
  const { t } = useTranslation();
  const links = ['Home','Courses','Tutors','About','Contact'];
  const social = [
    { url: profile?.instagram_url, icon:'📸', label:'Instagram' },
    { url: profile?.telegram_url, icon:'💬', label:'Telegram' },
    { url: profile?.facebook_url, icon:'👥', label:'Facebook' },
    { url: profile?.youtube_url, icon:'📺', label:'YouTube' },
    { url: profile?.tiktok_url, icon:'🎵', label:'TikTok' },
  ].filter(s => s.url);
  return (
    <footer style={{ background: C.navy, padding:'48px 24px 24px', color:'rgba(255,255,255,0.6)' }}>
      <div style={{ maxWidth: 1100, margin:'0 auto' }}>
        <div style={{ display:'flex', gap: 40, flexWrap:'wrap', marginBottom: 32 }}>
          <div style={{ flex:'0 0 220px' }}>
            <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius:'50%', background: profile?.primary_colour || C.teal, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight: 700, fontSize: 16 }}>{(profile?.name||'S').charAt(0)}</div>
              <span style={{ color:'#fff', fontWeight: 700, fontSize: 16 }}>{profile?.name || 'English School'}</span>
            </div>
            {profile?.address?.line1 && <p style={{ fontSize: 13, lineHeight: 1.7, margin:'0 0 8px' }}>{profile.address?.line1}{profile.city && `, ${profile.city}`}</p>}
          </div>
          <div>
            <div style={{ color:'#fff', fontWeight: 600, marginBottom: 12 }}>Quick Links</div>
            {links.map(l => <div key={l} style={{ marginBottom: 8 }}><a href={`#${l.toLowerCase()}`} style={{ color:'rgba(255,255,255,0.6)', textDecoration:'none', fontSize: 14 }}>{l}</a></div>)}
          </div>
          {social.length > 0 && (
            <div>
              <div style={{ color:'#fff', fontWeight: 600, marginBottom: 12 }}>Follow Us</div>
              <div style={{ display:'flex', gap: 12 }}>
                {social.map(s => <a key={s.label} href={s.url} target="_blank" rel="noreferrer" style={{ fontSize: 24, textDecoration:'none' }} title={s.label}>{s.icon}</a>)}
              </div>
            </div>
          )}
        </div>
        <div style={{ borderTop:`1px solid rgba(255,255,255,0.1)`, paddingTop: 20, display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap: 8, fontSize: 13 }}>
          <span>© 2026 {profile?.name || 'School'}. All rights reserved.</span>
          {tier === 1 && <a href="https://speakiq.uk" target="_blank" rel="noreferrer" style={{ color: C.teal, textDecoration:'none' }}>{t('school_powered_by')}</a>}
        </div>
      </div>
    </footer>
  );
}


function JoinAsTutorSection({ pc, subdomain }) {
  return (
    <section id="join-tutor" style={{ padding:'clamp(40px,8vw,72px) clamp(16px,4vw,24px)', background:'linear-gradient(135deg, #064E3B 0%, #065F46 100%)' }}>
      <div style={{ maxWidth: 900, margin:'0 auto', textAlign:'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>👩‍🏫</div>
        <h2 style={{ color:'#fff', fontSize: 32, fontWeight: 800, margin:'0 0 16px' }}>Become a Tutor</h2>
        <p style={{ color:'rgba(255,255,255,0.85)', fontSize: 18, maxWidth: 600, margin:'0 auto 16px', lineHeight: 1.6 }}>
          Share your expertise and help students reach their English goals. Join our growing team of qualified tutors.
        </p>
        <div style={{ display:'flex', justifyContent:'center', gap: 32, flexWrap:'wrap', margin:'32px 0', color:'rgba(255,255,255,0.9)', fontSize: 15 }}>
          {[['💰','Competitive pay per lesson'],['📅','Flexible schedule'],['📱','Online & in-person'],['🎓','Ongoing training']].map(([icon, text]) => (
            <div key={text} style={{ display:'flex', alignItems:'center', gap: 8 }}>
              <span style={{ fontSize: 20 }}>{icon}</span> {text}
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap: 14, justifyContent:'center', flexWrap:'wrap' }}>
          <a href="/tutor-signup" style={{ background:'#10B981', color:'#fff', textDecoration:'none', padding:'14px 32px', borderRadius: 8, fontWeight: 700, fontSize: 16 }}>
            Apply Now →
          </a>
          <a href="https://tutors.speakiq.uk/login" style={{ background:'rgba(255,255,255,0.15)', color:'#fff', textDecoration:'none', padding:'14px 32px', borderRadius: 8, fontWeight: 600, fontSize: 16, border:'1px solid rgba(255,255,255,0.3)' }}>
            Tutor Login
          </a>
        </div>
      </div>
    </section>
  );
}

export default function SchoolHomepage({ forceSubdomain }) {
  const { t } = useTranslation();
  const { subdomain: paramSubdomain } = useParams();
  const subdomain = forceSubdomain || paramSubdomain;
  const [profile, profileErr] = useApi(subdomain ? `/public/${subdomain}/profile` : null);
  const [stats] = useApi(subdomain ? `/public/${subdomain}/stats` : null);
  const [tutors] = useApi(subdomain ? `/public/${subdomain}/tutors` : null);
  const [packages] = useApi(subdomain ? `/public/${subdomain}/packages` : null);
  const loading = !profile && !profileErr;

  useEffect(() => {
    if (profile) {
      document.title = profile.meta_title || profile.name || 'English School';
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.name='description'; document.head.appendChild(metaDesc); }
      metaDesc.content = profile.meta_description || '';
    }
  }, [profile]);

  if (loading) return <Spinner />;

  if (profileErr || !profile || profile.website_enabled === false) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f9fafb', padding: 24 }}>
        <div style={{ textAlign:'center', maxWidth: 400 }}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🔒</div>
          <h2 style={{ color: C.navy, fontSize: 24, marginBottom: 12 }}>Website Not Available</h2>
          <p style={{ color: C.grey, fontSize: 16, lineHeight: 1.6, marginBottom: 24 }}>School website not available. Contact the school directly.</p>
          <div style={{ background:'#fff', border:`1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
            <p style={{ color: C.grey, fontSize: 14, marginBottom: 16 }}>Know the school's name? Search here:</p>
            <input placeholder="School name…" style={{ width:'100%', padding:'10px 14px', border:`1px solid ${C.border}`, borderRadius: 8, fontSize: 14, boxSizing:'border-box' }} />
          </div>
        </div>
      </div>
    );
  }

  const pc = profile.primary_colour || C.teal;
  const tier = profile.tier || 1;

  return (
    <div style={{ fontFamily:'Inter, system-ui, sans-serif', color: C.navy, overflowX:'hidden', width:'100%', maxWidth:'100vw' }}>
      <NavBar profile={profile} subdomain={subdomain} tier={tier} />
      <HeroSection profile={profile} stats={stats} subdomain={subdomain} />
      <WhyUs pc={pc} />
      <CoursesSection packages={packages} showPrices={profile.show_prices_public} subdomain={subdomain} pc={pc} />
      {profile.website_currency_converter !== false && <PublicConverter pc={pc} />}
      <HowItWorks />
      <TutorsSection tutors={tutors || []} subdomain={subdomain} pc={pc} />
      <JoinAsTutorSection pc={pc} subdomain={subdomain} />
      {profile.testimonials?.length > 0 && <TestimonialsSection testimonials={profile.testimonials} />}
      <CEFRPath />
      <AboutSection profile={profile} />
      <IELTSSection pc={pc} />
      <FindUs profile={profile} />
      <EnquiryForm subdomain={subdomain} packages={packages} />
      <MockTestsPublic subdomain={subdomain} />
      <Footer profile={profile} tier={tier} />
    </div>
  );
}
